import { DateTime } from "luxon";
import { TMainTrackTimeFilter, TSelectedVariants, TTrackTypes } from "@/types";

export function formatDateWithSuffix(date: Date) {
  const day = date.getDate();
  const suffix = (day: number) => {
    if (day >= 11 && day <= 13) {
      return "th";
    }
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const dayWithSuffix = day + suffix(day);
  const formattedDate = DateTime.fromJSDate(date).toFormat(`ccc, '${dayWithSuffix}' LLLL yyyy`);
  return formattedDate;
}

export const sortBySku = (variants: TSelectedVariants[], gameType: TTrackTypes) => {
  return variants.slice().sort((a: TSelectedVariants, b: TSelectedVariants) => {
    if (gameType === "MAIN_TRACK") {
      if (a.variantsSelectedObject.sku && b.variantsSelectedObject.sku) {
        const skuA = parseInt(a.variantsSelectedObject.sku.replace(".R", ""), 10);
        const skuB = parseInt(b.variantsSelectedObject.sku.replace(".R", ""), 10);
        return skuA - skuB;
      }
    }
    return 0;
  });
};

export const hasDifferentVariantSelectedTime = (variants: TSelectedVariants[], latestVariant: TSelectedVariants) => {
  return variants.some(
    (xFilterItem: TSelectedVariants) => !DateTime.fromISO(xFilterItem.variantSelectedTime.toISOString()).equals(DateTime.fromISO(latestVariant.variantSelectedTime.toISOString())),
  );
};

export const handleHourFromTitle = (title: string) => {
  const titleSplit = title.includes("/") ? title.split("/") : title;
  const hour = Array.isArray(titleSplit) ? titleSplit[2] : titleSplit;

  return hour;
};

export const handleSpaceBeforeAMOrPM = (timeString: string) => {
  const regex = /(PM|AM)$/i;

  return timeString.replace(regex, " $1");
};

export const getRaceHourFromSku = (sku: string | undefined) => {
  // SKUs in format YYMMDDHHMM
  if (!sku) {
    return 0;
  }

  let raceHour;
  if (sku[0] === "0") {
    raceHour = sku.slice(7, 9);
  } else {
    raceHour = sku.slice(6, 8);
  }

  const ret = parseInt(raceHour);
  return ret;
};

export const getRaceMinutesFromSku = (sku: string | undefined): number => {
  if (!sku) {
    return 0;
  }

  if (sku[0] === "0") {
    const raceHour = parseInt(sku.slice(7, 9));
    const raceMinutes = sku.slice(9, 11);
    return raceHour * 60 + parseInt(raceMinutes);
  }

  const raceHour = parseInt(sku.slice(6, 8));
  const raceMinutes = sku.slice(8, 10);
  return raceHour * 60 + parseInt(raceMinutes);
};

/**
 * Extracts the session calendar date encoded in a variant SKU.
 * Session SKUs are `yyMMddHHmm` (Mini Track SKUs carry a leading "0" and race
 * SKUs may carry a ".R<n>" suffix). Returns null when the SKU has no
 * parseable date (e.g. arcade cards or membership products).
 */
export const getDateFromVariantSku = (sku: string | undefined | null): DateTime | null => {
  if (!sku) {
    return null;
  }

  let datePart = sku.split(".")[0];
  if (datePart.length === 11 && datePart.startsWith("0")) {
    datePart = datePart.slice(1);
  }

  if (!/^\d{10}$/.test(datePart)) {
    return null;
  }

  const parsed = DateTime.fromFormat(datePart.slice(0, 6), "yyMMdd");
  return parsed.isValid ? parsed : null;
};

/**
 * Returns false only when the variant SKU provably encodes a different
 * calendar day than the given date. SKUs without a parseable date and
 * invalid dates return true — they cannot be validated, so they are never
 * blocked on date grounds.
 */
export const variantSkuMatchesDay = (sku: string | undefined | null, date: Date | string): boolean => {
  const skuDate = getDateFromVariantSku(sku);
  if (!skuDate) {
    return true;
  }

  const intendedDate = DateTime.fromJSDate(typeof date === "string" ? new Date(date) : date);
  if (!intendedDate.isValid) {
    return true;
  }

  return skuDate.hasSame(intendedDate, "day");
};

export const getStartTimeAndEndTime = (timeFilter: TMainTrackTimeFilter | "AFTERNOON" | "WHOLE_DAY" | "LUNCH" | "ARVO", track: TTrackTypes) => {
  const getMainTrackTimes = () => {
    switch (timeFilter) {
      case "MORNING":
        return { startHour: 7, endHour: 11 };
      case "LUNCH":
        return { startHour: 11, endHour: 15 };
      case "ARVO":
        return { startHour: 15, endHour: 19 };
      case "EVENING":
        return { startHour: 19, endHour: 23 };
      case "LATE":
        return { startHour: 23, endHour: 25 };
      case "WHOLE_DAY":
        return { startHour: 7, endHour: 25 };
      default:
        return { startHour: 7, endHour: 25 };
    }
  };

  const getJuniorTrackTimes = () => {
    switch (timeFilter) {
      case "MORNING":
        return { startHour: 7, endHour: 11 };
      case "LUNCH":
        return { startHour: 11, endHour: 15 };
      case "ARVO":
        return { startHour: 15, endHour: 19 };
      case "EVENING":
        return { startHour: 19, endHour: 23 };
      case "LATE":
        return { startHour: 23, endHour: 25 };
      case "WHOLE_DAY":
        return { startHour: 7, endHour: 25 };
      default:
        return { startHour: 7, endHour: 25 };
    }
  };

  const getMiniTrackTimes = () => {
    switch (timeFilter) {
      case "MORNING":
        return { startHour: 7, endHour: 11 };
      case "LUNCH":
        return { startHour: 11, endHour: 15 };
      case "ARVO":
        return { startHour: 15, endHour: 19 };
      case "WHOLE_DAY":
        return { startHour: 7, endHour: 19 };
      default:
        return { startHour: 7, endHour: 19 };
    }
  };

  const getBattleKartTimes = () => {
    switch (timeFilter) {
      case "MORNING":
        return { startHour: 7, endHour: 11 };
      case "LUNCH":
        return { startHour: 11, endHour: 15 };
      case "ARVO":
        return { startHour: 15, endHour: 19 };
      case "EVENING":
        return { startHour: 19, endHour: 23 };
      case "LATE":
        return { startHour: 23, endHour: 25 };
      case "WHOLE_DAY":
        return { startHour: 7, endHour: 25 };
      default:
        return { startHour: 7, endHour: 25 };
    }
  };

  switch (track) {
    case "MAIN_TRACK":
      return getMainTrackTimes();
    case "JUNIOR_TRACK":
      return getJuniorTrackTimes();
    case "MINI_TRACK":
      return getMiniTrackTimes();
    case "BATTLE_KART_TRACK":
      return getBattleKartTimes();
    default:
      return getMainTrackTimes();
  }
};

/**
 * Converts a hex color to RGB values
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
};

/**
 * Converts RGB values to hex color
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const interpolateColours = (hex1: string, hex2: string, percentage: number): string => {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);

  const r = rgb1.r + (rgb2.r - rgb1.r) * percentage;
  const g = rgb1.g + (rgb2.g - rgb1.g) * percentage;
  const b = rgb1.b + (rgb2.b - rgb1.b) * percentage;

  return rgbToHex(r, g, b);
};

export const base64Encode = (data: string) => {
  // Check if we're in a browser environment
  if (typeof window !== "undefined" && window.btoa) {
    return window.btoa(data);
  }

  // Check if we're in a Node.js environment with Buffer available
  if (typeof Buffer !== "undefined") {
    return Buffer.from(data, "utf8").toString("base64");
  }

  // Fallback for environments without either (shouldn't happen in practice)
  throw new Error("Base64 encoding not available in this environment");
};

export const base64Decode = (data: string) => {
  if (typeof window !== "undefined" && window.atob) {
    return window.atob(data);
  }

  if (typeof Buffer !== "undefined") {
    return Buffer.from(data, "base64").toString("utf8");
  }

  throw new Error("Base64 decoding not available in this environment");
};

export function getOrdinalSuffix(day: number) {
  // Handle the special cases for 11th, 12th, 13th
  if (day > 3 && day < 21) return "th";
  // Handle the general cases
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}
